// @TODO: when user clicks "start demo" on any page, redirect to this one
  // @TODO: if unauthenticated, have two options...sign up (big and obvious) sign in (small)...get arg should determine which one is current (actually...sign in and sign up are the same...don't need both flows)
/*
 * Ryan O'Dowd
 * 2024-01-22
 * © Copyright 2024 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import React, {
  useEffect,
  useState,
} from 'react';
import {
  format as dateFnsFormat,
  formatDistanceToNow as dateFnsFormatDistanceToNow,
} from 'date-fns';
import {
  fetchProfilePictureUrl,
  fetchSchool,
} from '@utils/store/actions';
import Accordion from '@components/common/Accordion';
import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import EditSchoolDialog from '@website/components/EditSchoolDialog';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Icon from '@react-native-vector-icons/material-design-icons';
import IconButton from '@components/common/IconButton';
import Image from '@components/Photo/Image';
import ManageSeatsDialog from './ManageSeatsDialog';
import SchoolPreview from '@website/components/SchoolPreview';
import ScreenWrapper from '@containers/ScreenWrapper';
import Text from '@components/common/Text';
import Tooltip from '@components/common/Tooltip';
import {
  View,
} from 'react-native';
import WebPageStub from '@website/containers/WebPageStub';
import getStyles from './styles';
import hooks from '@hooks';

const SCHOOL_LOGO_PLACEHOLDER_IMAGE = require('@assets/img/avatar.png'); // @TODO: replace with school (jaguar)

const School = () => {
  const styles = hooks.useStyles(getStyles);
  const dispatch = hooks.useTypedDispatch();
  const locationState = hooks.useLocationState();

  const [_show404, setShow404] = useState(false); // @TODO: shouldn't need this
  const [_isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [_isManageSeatsDialogOpen, setIsManageSeatsDialogOpen] = useState(false);

  const school = hooks.useTypedSelector((state) => state.schools.find((school) => school.id === locationState.schoolId));

  useEffect(() => {
    setTimeout(() => setShow404(true), 4000);
  }, []);

  useEffect(() => {
    dispatch(fetchSchool(locationState.schoolId));
  }, [dispatch, locationState.schoolId]);

  const logoUrl = hooks.useGetPhotoUrl(`school_logo::${school?.id}`); // @TODO: this format is too magical

  if (!school?.name && _show404) {
    return ( // @TODO: need this?
      <WebPageStub>
        <View style={styles.errorTextWrapper}>
          <Text style={[styles.errorText, styles.bold]}>This school could not be found.</Text>
          <Text style={styles.errorText}>Please check back later.</Text>{/* @TODO: update */}
        </View>
      </WebPageStub>
    );
  }

  if (!school?.billing?.transactions) { // @TODO: just incase this is ryan who already has billingless schools in redux
    return <Text>Loading...</Text>; // @TODO: better loading screen
  }

  const isInTrial = new Date(school.billing.trial_period_ends_at).getTime() > Date.now();

  const Stat = (statProps) => { // @TODO: move to its own component
    const item = (
      <View style={styles.stat}>
        <Text style={styles.widgetText}>{statProps.label}: </Text>
        <Text style={styles.highlightedText}>{statProps.value}</Text>
      </View>
    );

    if (!statProps.tooltipTitle) {
      return item;
    }

    return (
      <Tooltip title={statProps.tooltipTitle}>
        {item}
      </Tooltip>
    );
  };

  const allClassroomTeachers = [...new Set(school.classrooms.map((classroom) => classroom.teacher.display_name))];

  const hasMissingFields = true; // @TODO: dynamic

  return (
    <WebPageStub
      isWebApp={true}// @TODO: this is false, but hide footer maybe?
      hideTabs={true}
      fullWidth={true}// @NOTE: this is okay because every page in web app should have ScreenWrapper in it // @TODO: is this true of schools pages too?
    >{/* @TODO: add prop for isApp or something so that background is always correct and to hide (or change) the nav options */}
      <ScreenWrapper>{/* @TODO: inner padding? needed when width <= maxWidth */}
        <FullScreenScrollView style={styles.container}>{/* @TODO: should be able to pass props to this to use same styles as inner for Admin container */}
          <Accordion
            defaultOpen={true}
            expandable={false}
            rightItem={
              <View>
                <IconButton
                  style={styles.editButton}
                  icon='pencil'
                  iconColor={styles.colors.primaryExtraLight}
                  onPress={() => setIsEditDialogOpen(true)}
                />{/* @TODO: should this have a badge prop? */}
                {hasMissingFields && <Badge size={12} color={styles.colors.alert} right={32} />}
              </View>
            }
            rightItemFloatTop={true}
            title={
              <View style={styles.logoAndNameWrapper}>
                <Image
                  style={styles.schoolLogoImage}
                  resizeMode='cover'
                  source={!logoUrl ? SCHOOL_LOGO_PLACEHOLDER_IMAGE : {
                    uri: logoUrl,
                    priority: 'normal',
                  }}
                  onError={() => {
                    // probably still in redux but not in the FastImage cache, so refetch here
                    dispatch(fetchProfilePictureUrl(school.id, true));
                  }}
                  username={school.name}
                />

                <View style={styles.nameAndWebsiteWrapper}>
                  <Text style={[styles.widgetText, styles.schoolNameText]}>{school.name}</Text>
                  {!!school.website_url && <Text style={styles.websiteText} isLink={true} onPress={() => {window.location = school.website_url;}}>{school.website_url}</Text>}{/* @TODO: this should be a link (add prop to Text component for web styling) */}
                </View>
              </View>
            }
          >
            <View style={styles.innerContent}>
              <Stat
                tooltipTitle='Admins can view/modify all school information including billing info.'
                label='Admins'// @TODO: singular if one
                value={2}// @TODO: don't hardcode // @TODO: list names instead of giving count
              />
              <Stat
                leftItem={<Tooltip title='Teachers can view/modify only the classrooms to which they are assigned.'><Icon name='information' color={styles.colors.white} size={14} /></Tooltip>}
                label={allClassroomTeachers.length === 1 ? 'Teacher' : 'Teachers'}// @TODO: i18n
                value={allClassroomTeachers.join(', ')}
              />
              <Stat
                leftItem={<Tooltip title={`Plan renews on ${dateFnsFormat(new Date(school.billing.renewal_date), 'MMMM d, yyyy')}`}><Icon name='information' color={styles.colors.white} size={14} /></Tooltip>}
                label='Seats used'
                value={`${school.classrooms.reduce((acc, classroom) => acc + classroom.student_count, 0)} out of ${school.billing.total_seats_paid_for_in_current_subscription}`}
              />

              <br />

              {isInTrial && <Text style={styles.trialEndsText}>{dateFnsFormatDistanceToNow(school.billing.trial_period_ends_at, {addSuffix: false})} remaining in trial</Text>}
            </View>

            {/* @TODO:
            <Text style={styles.widgetText}>Impact: <Text style={styles.highlightedText}>TODO</Text></Text>
            {/* @TODO: see how many verses students here have reviewed...include students no longer part of school?
            */}

            <View style={styles.buttons}>
              <Button
                theme={styles.colors.accent}
                onPress={() => setIsManageSeatsDialogOpen(true)}
              >
                Add seats
              </Button>
            </View>
          </Accordion>

          <SchoolPreview
            key={school.id}
            title='Classrooms'
            school={school}
            defaultOpen={true}
          />{/* @TODO: update this to be reused between wrinkly admins and school admins */}
          {/* @TODO: empty screen for starting their first classroom */}
          {/* @TODO: insert accordions for all classrooms with a swich for showing past classrooms */}

          {/* @TODO:
          <h2>Fundraising campaigns</h2>
          <Text>TODO: fundraisers</Text>
          */}
          {/* @TODO: empty screen for starting their first campaign */}
          {/* @TODO: insert accordions for all campaigns with a swich for showing past campaigns */}

          {_isEditDialogOpen &&
            <EditSchoolDialog
              school={school}
              onClose={() => setIsEditDialogOpen(false)}
            />
          }
          {_isManageSeatsDialogOpen && <ManageSeatsDialog school={school} onClose={() => setIsManageSeatsDialogOpen(false)} isInTrial={isInTrial} />}
        </FullScreenScrollView>
      </ScreenWrapper>
    </WebPageStub>
  );
};

export default School;
